import * as React from 'react';
import { useTranslation } from '@shared/core/i18n';
import type { StylingProps } from './PeopleListItem.styles';
import {
  StyledItemInfo,
  AvatarWrapper,
  AvatarBorder,
  ItemText,
  ItemSubtext,
  TextContainer,
  DeliveryIconsRow,
  DeliveryIcon,
  UnreachableRowIndicator,
} from './PeopleListItem.styles';
import { Avatar, AvatarImage, AvatarFallback, Flex, TooltipV2 } from '@withjoy/joykit';
import { Chat, Envelope, WarningTriangle, WhatsApp } from '@withjoy/joykit/icons';
import { WHATSAPP } from '@shared/utils/messaging/messagingChannels.constants';
import type { PhoneChannel } from '@shared/utils/messaging/deliveryChannel.types';

export type InGroupPosition = 'first' | 'middle' | 'last' | '';

type SimpleProps = Readonly<{
  avatarUrl?: string;
  labelText: string;
  sublabelText?: string;
  disabled?: boolean;
  inGroupPosition?: InGroupPosition;
  isSmall?: boolean;
  compact?: boolean;
  showSublabelWhenSmall?: boolean;
  avatarName?: string;
  rightContent?: React.ReactNode;
}>;

const getAvatarSize = (isSmall?: boolean, compact?: boolean): 'sm' | 'md' | 'lg' | number => {
  if (compact) return isSmall ? 20 : 'sm';
  return isSmall ? 'sm' : 'md';
};

const Simple: React.FC<SimpleProps> = React.memo(
  ({ avatarUrl, labelText, sublabelText, disabled, isSmall, compact, showSublabelWhenSmall, inGroupPosition, avatarName, rightContent }) => {
    const { tq } = useTranslation('peoplePicker');
    const isNoNamePlusOne = inGroupPosition !== 'first' && !avatarName;
    const fallbackContent = avatarName ? undefined : inGroupPosition !== 'first' ? '+1' : '?';
    const subProps: StylingProps = { isSmall, compact, inGroupPosition, disabled, isNoNamePlusOne };
    return (
      <StyledItemInfo {...subProps}>
        <AvatarWrapper>
          <Avatar
            size={getAvatarSize(isSmall, compact)}
            name={avatarName || undefined}
          >
            {avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                alt={tq`${labelText} photo`}
              />
            ) : null}
            <AvatarFallback>{fallbackContent}</AvatarFallback>
          </Avatar>
          {inGroupPosition && <AvatarBorder />}
        </AvatarWrapper>
        <Flex
          flexDirection="column"
          overflow="hidden"
          flex={1}
        >
          <TextContainer>
            <ItemText>{labelText}</ItemText>
            {(!isSmall || showSublabelWhenSmall) && sublabelText && <ItemSubtext>{sublabelText}</ItemSubtext>}
          </TextContainer>
        </Flex>
        {rightContent}
      </StyledItemInfo>
    );
  },
);

export type WithDeliveryMethodProps = SimpleProps &
  Readonly<{
    id: string;
    selected: boolean;
    enableSmsSelection: boolean;
    email?: string;
    hasPhoneNumber?: boolean;
    /** contact_service marked the number `invalid`. Adds a warning beside the text icon; selection is untouched. */
    phoneInvalid?: boolean;
    /**
     * The channel the phone gets (MSG-584): WhatsApp shows its icon in place of the chat bubble, and
     * `invalid` warns like `phoneInvalid`. Unset, the row renders as before.
     */
    phoneChannel?: PhoneChannel;
    hasEmailDeliveryMethodSelected?: boolean;
    hasTextDeliveryMethodSelected?: boolean;
    shouldHideEmailIcon?: boolean;
    shouldHidePhoneIcon?: boolean;
  }>;

const WithDeliveryMethod: React.FC<WithDeliveryMethodProps> = React.memo(
  ({
    id,
    selected,
    email,
    enableSmsSelection,
    hasPhoneNumber,
    phoneInvalid,
    phoneChannel,
    hasEmailDeliveryMethodSelected,
    hasTextDeliveryMethodSelected,
    shouldHideEmailIcon,
    shouldHidePhoneIcon,
    labelText,
    ...baseProps
  }) => {
    const { tq } = useTranslation('peoplePicker');
    const isUnreachable = selected ? !hasEmailDeliveryMethodSelected && !hasTextDeliveryMethodSelected : !email && !hasPhoneNumber;

    const unreachableTooltip = isUnreachable ? (!hasPhoneNumber && !email ? tq`No contact information` : !hasPhoneNumber ? tq`No phone number` : tq`No email address`) : '';

    const showTextIcon = enableSmsSelection && !isUnreachable && !shouldHidePhoneIcon && !!hasPhoneNumber;
    const isWhatsApp = phoneChannel === 'whatsapp';
    const showPhoneWarning = !!phoneInvalid || phoneChannel === 'invalid';

    const deliveryIcons = (
      <DeliveryIconsRow>
        {showTextIcon && showPhoneWarning && (
          <TooltipV2
            content={tq`This number may not receive texts`}
            placement="top"
          >
            <UnreachableRowIndicator aria-label={tq`This number may not receive texts`}>
              <WarningTriangle size={14} />
            </UnreachableRowIndicator>
          </TooltipV2>
        )}
        {showTextIcon &&
          (isWhatsApp ? (
            <DeliveryIcon aria-label={hasTextDeliveryMethodSelected ? tq`${WHATSAPP}: sending` : tq`${WHATSAPP}: available`}>
              <WhatsApp size={16} />
            </DeliveryIcon>
          ) : (
            <DeliveryIcon aria-label={hasTextDeliveryMethodSelected ? tq`Text: sending` : tq`Text: available`}>
              <Chat size={16} />
            </DeliveryIcon>
          ))}
        {!isUnreachable && !shouldHideEmailIcon && email && (
          <DeliveryIcon aria-label={hasEmailDeliveryMethodSelected ? tq`Email: sending` : tq`Email: available`}>
            <Envelope size={16} />
          </DeliveryIcon>
        )}
        {isUnreachable && (
          <TooltipV2
            content={unreachableTooltip}
            placement="top"
          >
            <UnreachableRowIndicator>
              <WarningTriangle size={14} />
            </UnreachableRowIndicator>
          </TooltipV2>
        )}
      </DeliveryIconsRow>
    );

    return (
      <Simple
        labelText={labelText}
        {...baseProps}
        rightContent={deliveryIcons}
      />
    );
  },
);

export const PeopleListItem = {
  Simple,
  WithDeliveryMethod,
};
